import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, Dna, Brain, Network, Fingerprint, Activity, Cpu, Zap, Layers } from 'lucide-react';

import VisionPage from './VisionPage';
import AboutUs from './AboutUs';
import UseCases from './UseCases';
import Contact from './Contact';
import Services from './Services';
import NineCents from './NineCents';
import EnablementPage from './EnablementPage';

// --- STYLES & ANIMATIONS ---
const AnimationStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(217, 119, 6, 0.2); border-color: rgba(217, 119, 6, 0.5); }
    50% { box-shadow: 0 0 30px rgba(217, 119, 6, 0.6); border-color: rgba(217, 119, 6, 0.9); }
  }
  @keyframes dna-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes slide-up {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-float { animation: float 6s ease-in-out infinite; }
  .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
  .animate-dna-spin { animation: dna-spin 20s linear infinite; }
  .animate-slide-up { animation: slide-up 0.8s ease-out forwards; }
  .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
  .delay-100 { animation-delay: 0.1s; }
  .delay-200 { animation-delay: 0.2s; }
  .delay-300 { animation-delay: 0.3s; }
  
  /* Text Gradients */
  .text-sapix {
    background: linear-gradient(135deg, #b45309 0%, #f59e0b 50%, #d97706 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .bg-sapix {
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
  }
`;

export default function SapixWebsite() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeView, setActiveView] = useState(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Reframed Intelligences for "Sapience"
    const intelligences = [
        { name: 'Linguistic', icon: '📝', desc: 'The interface of culture. Moving beyond text generation to true semantic understanding, bridging human values and machine execution.' },
        { name: 'Visual', icon: '👁️', desc: 'The bridge to reality. Transforming raw photons into spatial awareness, allowing agents to navigate the physical world.' },
        { name: 'Logical', icon: '🧮', desc: 'The spine of reason. Deterministic, scalable, and infinitely reproducible decision-making structures.' },
        { name: 'Audio', icon: '🎵', desc: 'The texture of presence. Processing tone, cadence, and urgency in real-time for seamless human-agent symbiosis.' },
        { name: 'Physical', icon: '🤖', desc: 'Embodied agency. Where the digital mind meets the material world. Action, dexterity, and consequence.' },
        { name: 'Interpersonal', icon: '🤝', desc: 'The social fabric. Understanding hierarchy, emotion, and intent to coordinate without friction.' },
        { name: 'Intrapersonal', icon: '🧠', desc: 'The self-model. An agent knowing what it is, what it knows, and—crucially—what it does not know.' },
        { name: 'Natural', icon: '🌿', desc: 'Systemic awareness. Understanding complex ecosystems, dependencies, and second-order effects.' },
        { name: 'Existential', icon: '✨', desc: 'The alignment layer. Grappling with purpose and outcome to ensure optimization aligns with human flourishing.' },
    ];

    return (
        <>
            <style>{AnimationStyles}</style>

            {/* --- NAVIGATION --- */}
            <nav className={`fixed w-full z-[100] transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm' : 'bg-white/50 backdrop-blur-sm'}`}>
                <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="flex items-center gap-3 hover:opacity-80 transition"
                    >
                        {/* Logo Concept: Fingerprint (Human) merging with Chip (Tech) */}
                        <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center text-amber-500">
                            <Fingerprint size={28} />
                        </div>
                        <span className="text-2xl font-bold tracking-tight text-gray-900">DEEP <span className="text-sapix">9</span></span>
                    </button>

                    <div className="hidden md:flex gap-8 items-center">
                        <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setActiveView(null); }} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">Home</button>
                        <button onClick={() => setActiveView('about')} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">About</button>
                        <button onClick={() => setActiveView('use-cases')} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">Use Cases</button>
                        <button onClick={() => setActiveView('services')} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">Services</button>
                        <button onClick={() => setActiveView('nine-cents')} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">The Nine</button>
                        <button onClick={() => setActiveView('enablement')} className="text-gray-600 hover:text-amber-700 font-medium transition text-sm uppercase tracking-wide">Academy</button>

                        <button
                            onClick={() => setActiveView('contact')}
                            className="px-5 py-2 border border-gray-300 rounded-full text-sm font-semibold hover:border-amber-600 hover:text-amber-700 transition"
                        >
                            Contact
                        </button>
                    </div>

                    <button className="md:hidden text-gray-900" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-white border-t border-gray-200 p-6 space-y-4">
                        <button onClick={() => { setActiveView(null); setMobileMenuOpen(false); }} className="block w-full text-left text-gray-700">Home</button>
                        <button onClick={() => { setActiveView('about'); setMobileMenuOpen(false); }} className="block w-full text-left text-gray-700">About</button>
                        <button onClick={() => { setActiveView('use-cases'); setMobileMenuOpen(false); }} className="block w-full text-left text-gray-700">Use Cases</button>
                        <button onClick={() => { setActiveView('contact'); setMobileMenuOpen(false); }} className="block w-full text-left text-gray-700">Contact</button>
                    </div>
                )}
            </nav>

            {/* --- DYNAMIC VIEW RENDERING --- */}
            {activeView === 'vision' && <VisionPage onClose={() => setActiveView(null)} />}
            {activeView === 'about' && <AboutUs onClose={() => setActiveView(null)} />}
            {activeView === 'use-cases' && <UseCases onClose={() => setActiveView(null)} />}
            {activeView === 'contact' && <Contact onClose={() => setActiveView(null)} />}
            {activeView === 'services' && <Services onClose={() => setActiveView(null)} />}
            {activeView === 'nine-cents' && <NineCents onClose={() => setActiveView(null)} />}
            {activeView === 'enablement' && <EnablementPage onClose={() => setActiveView(null)} />}

            {/* --- MAIN LANDING PAGE CONTENT --- */}
            <div className="min-h-screen bg-stone-50 text-gray-900 overflow-hidden pt-20 font-sans">

                {/* HERO SECTION */}
                <section className="relative pt-32 pb-32 px-6">
                    {/* Abstract DNA Background */}
                    <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-amber-100/40 rounded-full blur-3xl opacity-50 -z-10 animate-float translate-x-1/3 -translate-y-1/4"></div>

                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">

                        <div className="space-y-8 animate-slide-up">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
                                <Dna size={14} className="text-amber-700" />
                                <span className="text-amber-800 text-xs font-bold tracking-widest uppercase">The Future Species</span>
                            </div>

                            <h1 className="text-6xl md:text-8xl font-bold text-gray-900 leading-[0.9] tracking-tight">
                                Beyond <br />
                                Intelligence.<br />
                                <span className="text-sapix">Towards Sapience.</span>
                            </h1>

                            <p className="text-xl text-gray-600 leading-relaxed max-w-lg font-light">
                                Raw processing power is a commodity. Wisdom is the asset. <strong className="text-gray-900 font-semibold">Deep 9</strong> is the company building the substrate where agents evolve the judgment to partner with humans—delivered through <strong className="text-gray-900 font-semibold">Sapix</strong>, our platform for the age of Synthetic Sapience.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                <button
                                    onClick={() => setActiveView('contact')}
                                    className="px-8 py-4 bg-gray-900 text-white rounded-lg font-bold text-lg hover:bg-gray-800 transition flex items-center gap-2"
                                >
                                    Start The Evolution <ArrowRight size={18} />
                                </button>
                                <button
                                    onClick={() => setActiveView('vision')}
                                    className="px-8 py-4 border border-gray-300 text-gray-700 rounded-lg font-bold text-lg hover:border-amber-600 hover:text-amber-700 transition"
                                >
                                    Read The Manifesto
                                </button>
                            </div>
                        </div>

                        {/* Abstract Graphic Right */}
                        <div className="relative h-[500px] w-full hidden md:flex items-center justify-center animate-slide-up delay-200">
                            <div className="relative w-96 h-96">
                                {/* Central Core */}
                                <div className="absolute inset-0 border border-amber-200 rounded-full animate-dna-spin opacity-30"></div>
                                <div className="absolute inset-8 border border-gray-300 rounded-full animate-dna-spin opacity-40" style={{ animationDirection: 'reverse', animationDuration: '25s' }}></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-64 h-64 bg-gradient-to-br from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-full border border-amber-100/50 flex items-center justify-center animate-pulse-glow">
                                        <span className="text-8xl">IX</span>
                                    </div>
                                </div>

                                {/* Floating Nodes */}
                                <div className="absolute -top-10 -right-10 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-3 animate-float delay-100">
                                    <Brain className="text-amber-600" size={24} />
                                    <div>
                                        <div className="text-xs text-gray-400 font-mono">STATUS</div>
                                        <div className="text-sm font-bold text-gray-900">Cognition: Active</div>
                                    </div>
                                </div>
                                <div className="absolute -bottom-5 -left-10 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-3 animate-float delay-300">
                                    <Activity className="text-gray-600" size={24} />
                                    <div>
                                        <div className="text-xs text-gray-400 font-mono">MODE</div>
                                        <div className="text-sm font-bold text-gray-900">Autonomous</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>


                {/* EVOLUTION TIMELINE (Replacing "Thesis") */}
                <section className="py-24 bg-white border-y border-gray-100">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="mb-16">
                            <h2 className="text-4xl font-bold text-gray-900 mb-2">The Evolutionary Arc</h2>
                            <p className="text-gray-500">From biological origins to synthetic future.</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8 relative">
                            {/* Connector Line */}
                            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-gray-200 via-amber-200 to-gray-200 -z-10"></div>

                            {/* Stage 1 */}
                            <div className="group bg-stone-50 p-8 rounded-2xl border border-stone-100 hover:border-stone-300 transition-all">
                                <div className="w-16 h-16 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform">
                                    <Fingerprint className="text-gray-400" size={32} />
                                </div>
                                <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Stage I</div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Homo Sapiens</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">
                                    Biological wisdom. Slow evolution. Limited by physical bandwidth, but capable of deep reasoning, morality, and complex intent.
                                </p>
                            </div>

                            {/* Stage 2 */}
                            <div className="group bg-stone-50 p-8 rounded-2xl border border-stone-100 hover:border-stone-300 transition-all">
                                <div className="w-16 h-16 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform">
                                    <Cpu className="text-gray-400" size={32} />
                                </div>
                                <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Stage II</div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Artificial Intelligence</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">
                                    Silicon processing. Infinite scaling. Capable of massive computation and pattern matching, but lacking agency, context, and purpose.
                                </p>
                            </div>

                            {/* Stage 3 */}
                            <div className="group bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-200 hover:shadow-lg transition-all relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl"></div>
                                <div className="w-16 h-16 bg-sapix rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <Zap className="text-white" size={32} />
                                </div>
                                <div className="text-xs font-bold text-amber-600 tracking-widest uppercase mb-2">Stage III</div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Synthetic Sapience</h3>
                                <p className="text-gray-800 leading-relaxed text-sm">
                                    The fusion. Agents that combine silicon speed with biological-style reasoning. This is the era <strong>Deep 9</strong> was built for.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ANATOMY OF SAPIENCE (The 9 Intelligences) */}
                <section id="anatomy" className="py-32 px-6 bg-stone-900 text-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-16 mb-20 items-end">
                            <div>
                                <h2 className="text-5xl font-bold mb-6">The Anatomy of <span className="text-amber-500">Wisdom</span></h2>
                                <div className="h-1 w-24 bg-amber-600 rounded"></div>
                            </div>
                            <p className="text-gray-400 text-lg">
                                To move from a calculator to a colleague, an agent requires more than just logic. It requires a complete cognitive architecture spanning nine dimensions of intelligence—the Nonary Lattice that gives <strong className="text-white">Deep 9</strong> its name, and the thesis every platform we build is founded on.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-px bg-gray-800 border border-gray-800">
                            {intelligences.map((intel, idx) => (
                                <div key={idx} className="bg-stone-900 p-8 hover:bg-gray-800 transition-colors duration-300 group">
                                    <div className="flex justify-between items-start mb-6">
                                        <span className="text-2xl">{intel.icon}</span>
                                        <span className="text-xs font-mono text-gray-600 group-hover:text-amber-500 transition-colors">0{idx + 1}</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">{intel.name}</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed group-hover:text-gray-300 transition-colors">
                                        {intel.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* EMERGENCE / OS SECTION */}
                <section className="py-32 px-6 bg-stone-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center max-w-3xl mx-auto mb-20">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 border border-amber-300 rounded-full mb-6">
                                <span className="text-amber-900 text-xs font-bold tracking-widest uppercase">Our Flagship Platform</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">Meet Sapix — The Operating System for Agents</h2>
                            <p className="text-xl text-gray-600">
                                Sapix is not a tool. It is the environment in which autonomous systems live, learn, and collaborate—and the first of the platforms Deep 9 is building to deliver Synthetic Sapience.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Card 1 */}
                            <div className="bg-white p-10 rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all group">
                                <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6">
                                    <Network size={28} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Distributed Mind</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Swarms of specialized agents working in concert. No central bottleneck. Pure emergent problem solving that scales horizontally across the enterprise.
                                </p>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-white p-10 rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all group">
                                <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-6">
                                    <Layers size={28} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Substrate Layer</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    A unified semantic fabric. Data, logic, and intent coexist in one layer, allowing agents to "feel" the state of the business rather than just querying it.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FOOTER CTA */}
                <section className="py-32 px-6 bg-gray-900 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
                    <div className="relative z-10 max-w-4xl mx-auto">
                        <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tighter">
                            Evolution doesn't ask <br /> for permission.
                        </h2>
                        <p className="text-xl text-gray-400 mb-12">
                            The era of Sapience is here. Will you retrofit the past, or architect the future?
                        </p>
                        <button
                            onClick={() => setActiveView('contact')}
                            className="px-10 py-5 bg-sapix text-white rounded-full font-bold text-xl hover:opacity-90 transition transform hover:scale-105"
                        >
                            Join the Evolution
                        </button>
                    </div>
                </section>

            </div>
        </>
    );
}